// src/components/games/crossroads/ChallengePlayerModal.tsx
import { logger } from '../../../utils/logger';
// Modal for selecting a friend to challenge to a Crossroads TCG game

import React, { useState, useEffect } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonButtons,
  IonIcon,
  IonList,
  IonItem,
  IonLabel,
  IonAvatar,
  IonNote,
  IonText,
  IonSpinner,
  IonSelect,
  IonSelectOption,
  useIonToast,
} from '@ionic/react';
import { close, gameControllerOutline, sendOutline } from 'ionicons/icons';
import { AvatarPreview } from '../../avatar/AvatarPreview';
import { sendMessage } from '../../../services/chatService';
import { useChatsData } from '../../../contexts/ChatsDataContext';

interface ChallengePlayerModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  gameType?: string; // Optional - kept for backwards compatibility
}

interface Match {
  id: string;
  otherUser: {
    id: string;
    username: string;
    display_name: string;
    avatar_url: string | null;
    avatar: any;
  };
}

// Available decks with display info
const DECK_OPTIONS = [
  { key: 'fire', name: 'Fire', emoji: '🔥', commander: 'Dice and Autism' },
  { key: 'water', name: 'Water', emoji: '💧', commander: 'Nic' },
  { key: 'earth', name: 'Earth', emoji: '🌱', commander: 'Carma' },
  { key: 'dark', name: 'Dark', emoji: '🌑', commander: 'Neal' },
  { key: 'light', name: 'Light', emoji: '☀️', commander: 'Brandy' },
  { key: 'fae', name: 'Fae', emoji: '✨', commander: 'Alli' },
  { key: 'wind', name: 'Wind', emoji: '💨', commander: 'Doug' },
];

export const ChallengePlayerModal: React.FC<ChallengePlayerModalProps> = ({
  isOpen,
  onDismiss,
  gameType,
}) => {
  const [presentToast] = useIonToast();
  const [sending, setSending] = useState(false);
  const [selectedMatchId, setSelectedMatchId] = useState<string | null>(null);
  const [selectedDeck, setSelectedDeck] = useState<string>('fire');

  // Get connections from context
  const { matches, loading } = useChatsData();

  const handleSendChallenge = async (match: Match) => {
    setSending(true);
    setSelectedMatchId(match.id);

    try {
      const deckInfo = DECK_OPTIONS.find(d => d.key === selectedDeck);

      // Create structured game invitation
      const invitationData = {
        type: 'CROSSROADS_GAME_INVITATION',
        challengerDeck: selectedDeck,
        gameType: gameType || `${deckInfo?.name} Deck Challenge`,
        timestamp: new Date().toISOString(),
      };

      // Send game invitation message with special format
      // The special marker helps us detect and render it differently
      const inviteMessage = `[GAME_INVITE:${JSON.stringify(invitationData)}]

🎮 **Crossroads TCG Challenge!** 🎴

I challenge you to a game of Crossroads: The Divergent Realms!

**My Deck:** ${deckInfo?.emoji} ${deckInfo?.name} (${deckInfo?.commander})

Choose your deck and accept to battle!`;

      const result = await sendMessage(match.id, inviteMessage);

      if (result.success) {
        presentToast({
          message: `Challenge sent to ${match.otherUser.display_name}!`,
          duration: 2000,
          position: 'top',
          color: 'success',
        });
        onDismiss();
      } else {
        presentToast({
          message: 'Failed to send challenge. Please try again.',
          duration: 2000,
          position: 'top',
          color: 'danger',
        });
      }
    } catch (error) {
      logger.error('Error sending challenge:', error);
      presentToast({
        message: 'Failed to send challenge. Please try again.',
        duration: 2000,
        position: 'top',
        color: 'danger',
      });
    } finally {
      setSending(false);
      setSelectedMatchId(null);
    }
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onDismiss}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Challenge a Friend</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onDismiss}>
              <IonIcon icon={close} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div style={{ padding: '16px' }}>
          <IonText>
            <h2>
              <IonIcon icon={gameControllerOutline} /> Crossroads TCG
            </h2>
            <p>Choose your deck and select a friend to challenge!</p>
          </IonText>

          {/* Deck Selection */}
          <IonItem style={{ marginBottom: '16px' }}>
            <IonLabel>Your Deck</IonLabel>
            <IonSelect
              value={selectedDeck}
              onIonChange={(e) => setSelectedDeck(e.detail.value)}
              interface="popover"
            >
              {DECK_OPTIONS.map((deck) => (
                <IonSelectOption key={deck.key} value={deck.key}>
                  {deck.emoji} {deck.name} - {deck.commander}
                </IonSelectOption>
              ))}
            </IonSelect>
          </IonItem>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <IonSpinner />
              <IonText>
                <p>Loading your connections...</p>
              </IonText>
            </div>
          ) : matches.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <IonText color="medium">
                <p>You don't have any connections yet.</p>
                <p>Match with someone first to challenge them!</p>
              </IonText>
            </div>
          ) : (
            <IonList>
              {matches.map((match) => (
                <IonItem
                  key={match.id}
                  button
                  onClick={() => !sending && handleSendChallenge(match)}
                  disabled={sending}
                >
                  <IonAvatar slot="start">
                    <AvatarPreview
                      avatar={match.otherUser.avatar}
                      size={40}
                    />
                  </IonAvatar>
                  <IonLabel>
                    <h2>{match.otherUser.display_name}</h2>
                    <IonNote>@{match.otherUser.username}</IonNote>
                  </IonLabel>
                  {sending && selectedMatchId === match.id ? (
                    <IonSpinner slot="end" />
                  ) : (
                    <IonIcon icon={sendOutline} slot="end" color="primary" />
                  )}
                </IonItem>
              ))}
            </IonList>
          )}
        </div>
      </IonContent>
    </IonModal>
  );
};
